import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Compass, AlertTriangle, Clock, UserPlus, Calendar, FileText, Target, ChevronRight, ChevronDown, ChevronUp, CheckCircle, User } from 'lucide-react';

const PAGE = 4;

// Chip stage (dimensione commerciale). trattativa include trattativa_lunga.
const STAGE_CHIPS = [
  { key: 'appuntamento', label: 'Appuntamento' },
  { key: 'sopralluogo', label: 'Sopralluogo' },
  { key: 'preventivazione', label: 'Preventivazione' },
  { key: 'presentato', label: 'Presentati' },
  { key: 'trattativa', label: 'Trattativa' },
];

const stageMatch = (item, key) => {
  if (key === 'trattativa') return item.sales_stage === 'trattativa' || item.sales_stage === 'trattativa_lunga';
  return item.sales_stage === key;
};

// Micro-informazione più utile per la riga (§14) — una sola.
const rowMeta = (it) => {
  if (it.needs_customer_followup) return { text: it.status_label || `Nessuna attività da ${it.days_without_customer_activity} giorni`, cls: 'text-[#C0392B]', Icon: AlertTriangle };
  if (it.is_new_assignment) return { text: `${it.status_label || 'Assegnato'} · da prendere in carico`, cls: 'text-[#1D6FD1]', Icon: UserPlus };
  if (it.has_future_customer_step && it.next_customer_step_at) {
    const d = new Date(it.next_customer_step_at);
    const lbl = d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
    return { text: `Prossimo step ${lbl}`, cls: 'text-[#0E8F86]', Icon: Calendar };
  }
  if (it.last_customer_followup_activity_at != null && it.days_without_customer_activity != null) {
    const n = it.days_without_customer_activity;
    return { text: n <= 0 ? 'Ultima attività oggi' : `Ultima attività ${n} ${n === 1 ? 'giorno' : 'giorni'} fa`, cls: 'text-gray-500', Icon: Clock };
  }
  return { text: it.stage_label, cls: 'text-gray-500', Icon: Compass };
};

const stageIcon = (stage) => {
  if (stage === 'preventivazione' || stage === 'presentato') return FileText;
  if (stage === 'trattativa' || stage === 'trattativa_lunga') return Target;
  if (stage === 'appuntamento' || stage === 'sopralluogo') return Calendar;
  return Compass;
};

const orderPriority = (items) => {
  const rank = (i) => {
    if (i.needs_customer_followup) return [0, -(i.days_without_customer_activity || 0)];
    if (i.is_new_assignment) return [1, 0];
    if (i.has_future_customer_step) return [2, 0];
    if (i.sales_stage === 'preventivazione') return [3, 0];
    if (['trattativa', 'trattativa_lunga', 'presentato'].includes(i.sales_stage)) return [4, 0];
    return [5, 0];
  };
  return [...items].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    return ra[0] - rb[0] || ra[1] - rb[1];
  });
};

const chipCls = (active, alert, muted) => {
  if (active) return 'bg-[#FF7819] text-white border-[#FF7819] font-semibold';
  if (alert) return 'bg-red-50 text-[#C0392B] border-red-200 hover:border-red-300';
  if (muted) return 'bg-white text-gray-400 border-gray-200 hover:border-[#FF7819]/40';
  return 'bg-white text-gray-600 border-gray-200 hover:border-[#FF7819]/40';
};

const MICRO = 'text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5';
const CHIP_BASE = 'flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs whitespace-nowrap transition-colors';

const Row = ({ it, pmMode, adminMode }) => {
  const meta = rowMeta(it);
  const SIcon = stageIcon(it.sales_stage);
  const MetaIcon = meta.Icon;
  return (
    <Link to={it.link || '#'} data-testid={`opp-lead-${it.lead_id}`}
      className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors group no-underline text-inherit">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-gray-100 text-gray-500">
          <SIcon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{it.client_name}</p>
          <p className="text-xs text-gray-500 truncate">
            <span className="font-medium">{it.stage_label}</span>
            {adminMode ? (
              it.responsibility === 'unassigned'
                ? <span className="text-[#C0392B]"> · PM non assegnato · Tecnico non assegnato</span>
                : it.responsibility === 'pm'
                  ? <span className="text-gray-400"> · PM: {it.pm_name} · <span className="text-[#008A3D]">Gestione diretta</span></span>
                  : <span className="text-gray-400"> · PM: {it.pm_name || 'n/d'} · Tecnico: {it.technician_name}</span>
            ) : pmMode ? (
              it.responsibility === 'diretta'
                ? <span className="text-[#008A3D]"> · Gestione diretta</span>
                : <span className="text-gray-400"> · Tecnico: {it.technician_name}</span>
            ) : (it.city ? <span className="text-gray-400"> · {it.city}</span> : null)}
          </p>
          <p className={`text-xs flex items-center gap-1 mt-0.5 ${meta.cls}`}>
            <MetaIcon className="w-3 h-3 shrink-0" />
            {it.needs_customer_followup ? (
              <span className="truncate">Nessuna attività da <span className="font-semibold">{it.days_without_customer_activity} {it.days_without_customer_activity === 1 ? 'giorno' : 'giorni'}</span></span>
            ) : (
              <span className="truncate">{meta.text}</span>
            )}
          </p>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors shrink-0" />
    </Link>
  );
};

const SimpleRow = ({ it }) => (
  <Link to={it.link || '#'} data-testid={`opp-assegnare-${it.lead_id}`}
    className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 transition-colors group no-underline text-inherit">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-orange-100 text-orange-600">
        <UserPlus className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{it.client_name}</p>
        <p className="text-xs text-gray-500 truncate">
          <span className="font-medium text-orange-700">Da assegnare</span>
          {it.city ? <span className="text-gray-400"> · {it.city}</span> : null}
        </p>
      </div>
    </div>
    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#FF7819] transition-colors shrink-0" />
  </Link>
);

export const FollowupOpportunities = ({
  title = 'Le mie opportunità',
  items = [], daAssegnare = [], technicians = [], pmMode = false,
  adminMode = false, pmOptions = [],
  filter = 'priority', setFilter, listRef, onExpandChange,
}) => {
  const [visible, setVisible] = useState(PAGE);
  const [resp, setResp] = useState('all');
  const [techId, setTechId] = useState('all');
  const [pmId, setPmId] = useState('all');
  const respMode = pmMode || adminMode;
  useEffect(() => { setVisible(PAGE); }, [filter, resp, techId, pmId]);
  useEffect(() => { onExpandChange && onExpandChange(visible > PAGE); }, [visible, onExpandChange]);

  // Responsibility (PM) / responsabilità globale (Admin): filtro trasversale
  const scoped = useMemo(() => {
    if (!respMode) return items;
    return items.filter((i) => {
      if (adminMode && pmId !== 'all' && i.project_manager_id !== pmId) return false;
      if (pmMode) {
        if (resp === 'diretta') return i.responsibility === 'diretta';
        if (resp === 'tecnico') return i.responsibility === 'tecnico' && (techId === 'all' || i.assigned_technician_id === techId);
        return true;
      }
      // adminMode
      if (resp === 'pm') return i.responsibility === 'pm';
      if (resp === 'tecnico') return i.responsibility === 'tecnico' && (techId === 'all' || i.assigned_technician_id === techId);
      if (techId !== 'all') return i.assigned_technician_id === techId;
      return true;
    });
  }, [items, respMode, pmMode, adminMode, resp, techId, pmId]);

  // Tecnici disponibili nello scope corrente (Admin: cascata dal PM selezionato)
  const techOptions = useMemo(() => {
    const src = items.filter((i) => i.responsibility === 'tecnico'
      && (!adminMode || pmId === 'all' || i.project_manager_id === pmId));
    const seen = new Map();
    src.forEach((i) => { if (i.assigned_technician_id && !seen.has(i.assigned_technician_id)) seen.set(i.assigned_technician_id, i.technician_name || 'Tecnico'); });
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [items, adminMode, pmId]);

  const counts = useMemo(() => ({
    priority: scoped.length,
    nuovi: scoped.filter((i) => i.is_new_assignment).length,
    da_ricontattare: scoped.filter((i) => i.needs_customer_followup).length,
    appuntamento: scoped.filter((i) => stageMatch(i, 'appuntamento')).length,
    sopralluogo: scoped.filter((i) => stageMatch(i, 'sopralluogo')).length,
    preventivazione: scoped.filter((i) => stageMatch(i, 'preventivazione')).length,
    presentato: scoped.filter((i) => stageMatch(i, 'presentato')).length,
    trattativa: scoped.filter((i) => stageMatch(i, 'trattativa')).length,
  }), [scoped]);

  const working = useMemo(() => {
    switch (filter) {
      case 'nuovi': return scoped.filter((i) => i.is_new_assignment);
      case 'da_ricontattare': return scoped.filter((i) => i.needs_customer_followup).sort((a, b) => {
        const ao = a.attese_scadute ? 1 : 0, bo = b.attese_scadute ? 1 : 0;
        if (ao !== bo) return bo - ao; // next step scaduti prima (§20)
        return (b.days_without_customer_activity || 0) - (a.days_without_customer_activity || 0);
      });
      case 'preventivazione_warn': return scoped.filter((i) => stageMatch(i, 'preventivazione') && i.needs_customer_followup);
      case 'trattativa_warn': return scoped.filter((i) => stageMatch(i, 'trattativa') && i.needs_customer_followup);
      case 'da_assegnare': return daAssegnare;
      case 'priority': return orderPriority(scoped);
      default: return scoped.filter((i) => stageMatch(i, filter));
    }
  }, [filter, scoped, daAssegnare]);

  const titleMap = {
    priority: 'Priorità', nuovi: 'Nuovi assegnati', da_ricontattare: 'Da seguire',
    appuntamento: 'Appuntamento', sopralluogo: 'Sopralluogo', preventivazione: 'Preventivazione',
    presentato: 'Presentati', trattativa: 'Trattativa',
    preventivazione_warn: 'Preventivazione · da seguire', trattativa_warn: 'Trattativa · da seguire',
    da_assegnare: 'Da assegnare',
  };
  const sectionTitle = filter === 'priority' ? 'Priorità' : `${titleMap[filter] || filter} · ${working.length}`;

  const controlChips = [
    { key: 'priority', label: 'Priorità', count: counts.priority },
    { key: 'nuovi', label: 'Nuovi', count: counts.nuovi },
    { key: 'da_ricontattare', label: 'Da seguire', count: counts.da_ricontattare },
  ];
  const faseChips = STAGE_CHIPS.map((s) => ({ key: s.key, label: s.label, count: counts[s.key] }));

  const shown = working.slice(0, visible);
  const isAssegnare = filter === 'da_assegnare';

  return (
    <Card className="bg-white rounded-xl border border-[#E4E4E4] shadow-sm h-full flex flex-col" data-testid="followup-opportunities">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Compass className="w-5 h-5 text-[#FF7819]" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0" ref={listRef}>
        {/* Filtri raggruppati: un solo divider in fondo, prima della lista */}
        <div className="pb-3 border-b border-gray-100 space-y-3">
          {/* RIGA 1 — CONTROLLO OPERATIVO */}
          <div data-testid="opp-group-controllo">
            <p className={MICRO}>Controllo</p>
            <div className="flex flex-wrap gap-1.5">
              {controlChips.map((c) => (
                <button key={c.key} onClick={() => setFilter && setFilter(c.key)} data-testid={`opp-chip-${c.key}`}
                  className={`${CHIP_BASE} ${chipCls(filter === c.key, false, c.count === 0)}`}>
                  <span>{c.label}</span><span className="font-bold">{c.count}</span>
                </button>
              ))}
              {(pmMode || adminMode) && daAssegnare.length > 0 && (
                <button onClick={() => setFilter && setFilter('da_assegnare')} data-testid="opp-chip-da_assegnare"
                  className={`${CHIP_BASE} ${chipCls(filter === 'da_assegnare', true, false)}`}>
                  <AlertTriangle className="w-3 h-3" /><span>Da assegnare</span><span className="font-bold">{daAssegnare.length}</span>
                </button>
              )}
            </div>
          </div>
          {/* RIGA 2 — FASE COMMERCIALE */}
          <div data-testid="opp-group-fase">
            <p className={MICRO}>Fase commerciale</p>
            <div className="flex flex-wrap gap-1.5">
              {faseChips.map((c) => (
                <button key={c.key} onClick={() => setFilter && setFilter(c.key)} data-testid={`opp-chip-${c.key}`}
                  className={`${CHIP_BASE} ${chipCls(filter === c.key, false, c.count === 0)}`}>
                  <span>{c.label}</span><span className="font-bold">{c.count}</span>
                </button>
              ))}
            </div>
          </div>
          {/* RIGA 3 — RESPONSABILITÀ (PM: diretta/tecnici · Admin: Gestione PM/Tecnici + PM dropdown) */}
          {respMode && (
            <div className="pt-1" data-testid="opp-resp-filters">
              <p className={MICRO}>Responsabilità</p>
              <div className="flex flex-wrap items-center gap-2">
                {(pmMode
                  ? [{ k: 'all', l: 'Tutti' }, { k: 'diretta', l: 'Gestione diretta' }, { k: 'tecnico', l: 'Tecnici' }]
                  : [{ k: 'all', l: 'Tutti' }, { k: 'pm', l: 'Gestione PM' }, { k: 'tecnico', l: 'Tecnici' }]
                ).map((r) => (
                  <button key={r.k} onClick={() => { setResp(r.k); if (r.k !== 'tecnico') setTechId('all'); }} data-testid={`opp-resp-${r.k}`}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${resp === r.k ? 'bg-[#3C3C3C] text-white border-[#3C3C3C]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                    {r.l}
                  </button>
                ))}
                {/* Admin: PM dropdown sempre visibile */}
                {adminMode && (
                  <select value={pmId} onChange={(e) => { setPmId(e.target.value); setTechId('all'); }} data-testid="opp-pm-select"
                    className="text-xs px-2 py-1 rounded-full border border-gray-200 bg-white text-gray-600 focus:outline-none focus:border-[#FF7819]">
                    <option value="all">PM: Tutti</option>
                    {pmOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
                {/* Tecnico dropdown: PM->nascosto (non pertinente); Admin GESTIONE PM->nascosto */}
                {((pmMode && resp === 'tecnico' && technicians.length > 0)
                  || (adminMode && resp !== 'pm' && techOptions.length > 0)) && (
                  <select value={techId} onChange={(e) => setTechId(e.target.value)} data-testid="opp-tecnico-select"
                    className="text-xs px-2 py-1 rounded-full border border-gray-200 bg-white text-gray-600 focus:outline-none focus:border-[#FF7819]">
                    <option value="all">Tecnico: Tutti</option>
                    {(pmMode ? technicians : techOptions).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
              </div>
            </div>
          )}
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mt-3 mb-1.5" data-testid="opp-section-title">{sectionTitle}</p>

        {working.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-center" data-testid="opp-empty">
            <div className="w-11 h-11 bg-gray-50 rounded-full flex items-center justify-center mb-2">
              <CheckCircle className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-600">
              {filter === 'priority' ? 'Nessuna attività prioritaria'
                : filter === 'nuovi' ? 'Nessun nuovo Lead assegnato'
                : (respMode && resp !== 'all') ? 'Nessuna opportunità per questo filtro'
                : `Nessun lead in "${titleMap[filter] || filter}"`}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-0.5" data-testid="opp-list">
              {shown.map((it) => isAssegnare
                ? <SimpleRow key={it.lead_id} it={it} />
                : <Row key={it.lead_id} it={it} pmMode={pmMode} adminMode={adminMode} />)}
            </div>
            {working.length > PAGE && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500" data-testid="opp-count">{Math.min(visible, working.length)} di {working.length}</span>
                <div className="flex items-center gap-3">
                  {visible < working.length && (
                    <button onClick={() => setVisible((v) => v + PAGE)} data-testid="opp-more"
                      className="text-xs font-medium text-[#FF7819] hover:underline flex items-center gap-1">
                      {working.length - visible === 1 ? 'Mostra altro 1' : `Mostra altri ${Math.min(PAGE, working.length - visible)}`} <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {visible > PAGE && (
                    <button onClick={() => setVisible(PAGE)} data-testid="opp-less"
                      className="text-xs font-medium text-gray-500 hover:underline flex items-center gap-1">
                      Riduci <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
